import React from 'react'
import clsx from 'clsx'
import { styled } from '@mui/material/styles'
import { makeStyles } from 'tss-react/mui'
import { Logger } from 'wdc-cube'
import { bannerSx, buttonSx, classToFComponent, FCClassContext, LifesHubColors } from 'src/utils/views'
import { TcmCard } from 'src/utils/views/modules/the-consumer-market'

import Alert from '@mui/material/Alert'
import Button from '@mui/material/Button'
import IconButton from '@mui/material/IconButton'
import InputBase from '@mui/material/InputBase'
import MenuItem from '@mui/material/MenuItem'
import Select, { SelectChangeEvent } from '@mui/material/Select'
import Tooltip from '@mui/material/Tooltip'

import CloseIcon from '@mui/icons-material/Close'
import ImageNotSupportedOutlinedIcon from '@mui/icons-material/ImageNotSupportedOutlined'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import SearchIcon from '@mui/icons-material/Search'
import StarRoundedIcon from '@mui/icons-material/StarRounded'

import { DataGrid, GridColDef, GridRowClassNameParams, GridRowParams } from '@mui/x-data-grid'

import TcmProgress from '../../the-consumer-market/views/helpers/tcm_progress'
import StyledDataGrid from '../../the-consumer-market/views/helpers/tcm_datagrid'
import { formatNumber } from '../../the-consumer-market/views/helpers/tcm_view_utils'
import { CategoriaTableRow, CategoriaTableScope, FotoScope, SearchBarScope, WordCloudScope } from '../mv_scopes'

const LOG = Logger.get('MV-Components')

export function LoadError({ message }: { message: string }) {
    if (!message) return null
    return (
        <Alert severity="error" sx={{ ...bannerSx('danger'), flexShrink: 0 }}>
            {message}
        </Alert>
    )
}

const useSearchBarStyles = makeStyles()({
    view: {
        display: 'flex',
        flexGrow: 1,
        alignItems: 'center',
        gap: 8,
        minHeight: 43,
        minWidth: 0,
        padding: '0 5px 0 13px',
        borderRadius: 9,
        border: '1px solid rgba(255, 255, 255, .18)',
        backgroundColor: 'rgba(255, 255, 255, .06)',
        transition: 'border-color .15s ease, box-shadow .15s ease',
        '&:focus-within': {
            borderColor: 'var(--green-700)',
            boxShadow: '0 0 0 3px rgba(38, 229, 31, .12)'
        }
    },
    icon: {
        fontSize: 20,
        color: 'rgba(255, 255, 255, .62)',
        flex: '0 0 auto'
    },
    input: {
        flexGrow: 1,
        minWidth: 0,
        fontSize: 14,
        color: '#f4f4f4',
        '& input::placeholder': {
            color: 'rgba(255, 255, 255, .52)',
            opacity: 1,
            textOverflow: 'ellipsis'
        }
    },
    clear: {
        color: 'rgba(255, 255, 255, .62)',
        '&:hover': { color: '#fff', backgroundColor: 'rgba(255, 255, 255, .08)' }
    },
    modo: {
        flex: '0 0 auto',
        fontSize: 13,
        color: 'rgba(255, 255, 255, .82)',
        paddingLeft: 10,
        borderLeft: '1px solid rgba(255, 255, 255, .18)',
        '& .MuiSelect-select': { paddingTop: 2, paddingBottom: 2 },
        '& .MuiSelect-icon': { color: 'rgba(255, 255, 255, .62)' },
        '@media (max-width: 960px)': { display: 'none' }
    }
})

type SearchBarViewProps = {
    className?: string
    scope: SearchBarScope
}

class SearchBarViewClass implements FCClassContext<SearchBarViewProps> {
    scope!: SearchBarScope
    inputEl?: HTMLInputElement

    onSyncState({ scope }: SearchBarViewProps) {
        this.scope = scope
    }

    readonly inputRef = (elm: HTMLInputElement | null) => {
        this.inputEl = elm ?? undefined
        if (elm) elm.value = this.scope.texto
    }

    readonly emitChange = (evt: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
        this.scope.onTextoChange(evt.target.value)
    readonly emitKeyDown = (evt: React.KeyboardEvent) => {
        if (evt.key === 'Enter') this.scope.onSubmit().catch(LOG.caught)
    }
    readonly emitSubmit = () => this.scope.onSubmit().catch(LOG.caught)
    readonly emitClear = () => {
        if (this.inputEl) {
            this.inputEl.value = ''
            this.inputEl.focus()
        }
        this.scope.onClear().catch(LOG.caught)
    }
    readonly emitModo = (evt: SelectChangeEvent) => this.scope.onModoChange(evt.target.value).catch(LOG.caught)

    render({ className, scope }: SearchBarViewProps) {
        const { classes } = useSearchBarStyles()
        if (this.inputEl && this.inputEl.value !== scope.texto && document.activeElement !== this.inputEl) {
            this.inputEl.value = scope.texto
        }
        return (
            <div className={clsx(classes.view, className)}>
                <SearchIcon className={classes.icon} aria-hidden="true" />
                <InputBase
                    className={classes.input}
                    inputRef={this.inputRef}
                    defaultValue={scope.texto}
                    onChange={this.emitChange}
                    onKeyDown={this.emitKeyDown}
                    placeholder={scope.placeholder}
                    inputProps={{ 'aria-label': scope.placeholder }}
                />
                {!!scope.texto && (
                    <IconButton
                        className={classes.clear}
                        size="small"
                        aria-label="Limpar busca"
                        onClick={this.emitClear}
                    >
                        <CloseIcon fontSize="small" />
                    </IconButton>
                )}
                {scope.modos.length > 0 && (
                    <Tooltip title="Onde procurar">
                        <Select
                            className={classes.modo}
                            variant="standard"
                            disableUnderline
                            value={scope.modo}
                            onChange={this.emitModo}
                            inputProps={{ 'aria-label': 'Onde procurar' }}
                        >
                            {scope.modos.map((modo) => (
                                <MenuItem key={modo.value} value={modo.value}>
                                    {modo.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </Tooltip>
                )}
                <Button sx={{ ...buttonSx('brand', true), flex: '0 0 auto' }} onClick={this.emitSubmit}>
                    Pesquisar
                </Button>
            </div>
        )
    }
}

export const SearchBarView = classToFComponent(SearchBarViewClass, React)

// ::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::
// :: Nuvem de palavras
// ::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::

const MAX_WORDS = 60
const MIN_FONT = 11
const MAX_FONT = 32

const WORD_TONES = ['#0c8f14', '#2f6b3a', '#5f9e70', '#3d4a42', '#6fbf8e', '#1e4e91', '#66706a']

const Cloud = styled('div')({
    position: 'relative',
    display: 'flex',
    flexWrap: 'wrap',
    alignContent: 'safe center',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '2px 10px',
    flexGrow: 1,
    minHeight: 0,
    overflow: 'hidden',
    padding: '4px 2px'
})

const Word = styled('button', { shouldForwardProp: (prop) => prop !== 'selected' })<{ selected?: boolean }>(
    ({ selected }) => ({
        border: 0,
        padding: '0 2px',
        background: selected ? LifesHubColors.warningBg : 'transparent',
        borderRadius: 6,
        lineHeight: 1.05,
        fontWeight: 700,
        cursor: 'pointer',
        whiteSpace: 'nowrap',
        transition: 'opacity .15s ease',
        '&:hover': { opacity: 0.7 }
    })
)

type WordCloudViewProps = {
    className?: string
    scope: WordCloudScope
}

const ESCALA_VAR = '--mv-word-escala'

class WordCloudViewClass implements FCClassContext<WordCloudViewProps> {
    scope!: WordCloudScope

    private cloudEl: HTMLDivElement | null = null
    private readonly resizeObserver = new ResizeObserver(() => this.ajustarEscala())

    onSyncState({ scope }: WordCloudViewProps) {
        this.scope = scope
    }

    onDetach() {
        this.resizeObserver.disconnect()
    }

    private readonly refs = new Map<string, (el: HTMLDivElement | null) => void>()

    private cloudRef(conteudo: string) {
        let ref = this.refs.get(conteudo)
        if (!ref) {
            this.refs.clear()
            ref = (el: HTMLDivElement | null) => {
                if (this.cloudEl && this.cloudEl !== el) this.resizeObserver.unobserve(this.cloudEl)
                this.cloudEl = el
                if (el) {
                    this.resizeObserver.observe(el)
                    this.ajustarEscala()
                }
            }
            this.refs.set(conteudo, ref)
        }
        return ref
    }

    private ajustarEscala() {
        const el = this.cloudEl
        if (!el) return
        const transborda = () => el.scrollHeight > el.clientHeight + 1 || el.scrollWidth > el.clientWidth + 1
        let escala = 1
        el.style.setProperty(ESCALA_VAR, '1')
        while (escala > 0.45 && transborda()) {
            escala = Math.max(0.45, escala - 0.07)
            el.style.setProperty(ESCALA_VAR, String(escala))
        }
    }

    render({ className, scope }: WordCloudViewProps) {
        const visiveis = scope.words.slice(0, MAX_WORDS)
        const words = arrangeWords(visiveis)
        const ultimo = Math.max(visiveis.length - 1, 1)
        const ref = this.cloudRef(`${scope.loaded}|${visiveis.map((w) => w.texto).join('|')}`)
        return (
            <TcmCard className={className} caption={scope.caption}>
                <Cloud ref={ref}>
                    {!scope.loaded && <TcmProgress />}
                    {scope.loaded && words.length === 0 && <EmptyText>Nenhuma palavra no recorte atual.</EmptyText>}
                    {scope.loaded &&
                        words.map((word) => {
                            const rank = visiveis.indexOf(word)
                            const base = MIN_FONT + Math.pow(1 - rank / ultimo, 2.2) * (MAX_FONT - MIN_FONT)
                            const fontSize = `max(9px, calc(var(${ESCALA_VAR}, 1) * ${Math.round(base)}px))`
                            return (
                                <Tooltip key={word.texto} title={`${word.texto}: ${formatNumber(word.peso)}`}>
                                    <Word
                                        type="button"
                                        selected={word.selected}
                                        style={{
                                            fontSize,
                                            color: word.selected
                                                ? LifesHubColors.warning
                                                : WORD_TONES[rank % WORD_TONES.length]
                                        }}
                                        onClick={() => scope.onWordClicked(word.texto).catch(LOG.caught)}
                                    >
                                        {word.texto}
                                    </Word>
                                </Tooltip>
                            )
                        })}
                </Cloud>
            </TcmCard>
        )
    }
}

function arrangeWords<T extends { peso: number }>(words: T[]) {
    const sorted = [...words].sort((a, b) => b.peso - a.peso)
    const result: T[] = []
    sorted.forEach((word, index) => (index % 2 === 0 ? result.push(word) : result.unshift(word)))
    return result
}

export const WordCloudView = classToFComponent(WordCloudViewClass, React)

const EmptyText = styled('div')({
    color: LifesHubColors.muted,
    fontSize: 13,
    textAlign: 'center',
    padding: 20
})

// ::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::
// :: Classificação da Internet (categorias do Google)
// ::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::

const GridBox = styled('div')({
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    minHeight: 0
})

const TotalRow = styled('div')({
    display: 'flex',
    justifyContent: 'space-between',
    padding: '8px 10px 0',
    borderTop: `1px solid ${LifesHubColors.border}`,
    fontWeight: 800,
    fontSize: 13
})

const CustomDataGrid = StyledDataGrid as typeof DataGrid

const CATEGORIA_COLUMNS: GridColDef[] = [
    { field: 'categoria', headerName: 'Categoria', flex: 1, minWidth: 160 },
    {
        field: 'total',
        headerName: 'Quantidade',
        width: 110,
        type: 'number',
        valueFormatter: (value) => formatNumber(Number(value))
    }
]

type CategoriaTableViewProps = {
    className?: string
    scope: CategoriaTableScope
}

class CategoriaTableViewClass implements FCClassContext<CategoriaTableViewProps> {
    scope!: CategoriaTableScope

    onSyncState({ scope }: CategoriaTableViewProps) {
        this.scope = scope
    }

    readonly emitRowClick = (params: GridRowParams) => {
        this.scope.onItemClicked(String(params.id)).catch(LOG.caught)
    }

    render({ className, scope }: CategoriaTableViewProps) {
        return (
            <TcmCard className={className} caption={scope.caption}>
                <GridBox>
                    {!scope.loaded && <TcmProgress />}
                    {scope.loaded && (
                        <>
                            <CustomDataGrid
                                rows={[...scope.rows]}
                                columns={CATEGORIA_COLUMNS}
                                density="compact"
                                hideFooter
                                disableColumnSelector
                                disableRowSelectionOnClick
                                onRowClick={this.emitRowClick}
                                getRowClassName={selectedRowClassName}
                            />
                            <TotalRow>
                                <span>Total</span>
                                <span>{formatNumber(scope.total)}</span>
                            </TotalRow>
                        </>
                    )}
                </GridBox>
            </TcmCard>
        )
    }
}

export const CategoriaTableView = classToFComponent(CategoriaTableViewClass, React)

export function selectedRowClassName(params: GridRowClassNameParams<CategoriaTableRow>) {
    return params.row.selected ? 'super-app-theme-selected' : ''
}

// ::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::
// :: Foto do estabelecimento (Foto Web + StreetView)
// ::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::

const FotoBox = styled('div')({
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexGrow: 1,
    minHeight: 180,
    borderRadius: 10,
    overflow: 'hidden',
    backgroundColor: LifesHubColors.surface2,
    border: `1px solid ${LifesHubColors.border}`,
    '& img': { width: '100%', height: '100%', objectFit: 'cover', position: 'absolute', inset: 0 }
})

const FotoVazia = styled('div')({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 6,
    color: LifesHubColors.muted,
    fontSize: 12.5,
    textAlign: 'center',
    padding: 16
})

const FotoInfo = styled('div')({
    display: 'grid',
    gap: 3,
    marginTop: 12,
    flexShrink: 0
})

const FotoNome = styled('div')({
    fontWeight: 800,
    fontSize: 15,
    color: LifesHubColors.ink,
    lineHeight: 1.25
})

const FotoMeta = styled('div')({
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 8,
    color: LifesHubColors.muted,
    fontSize: 12.5,
    '& .MuiSvgIcon-root': { fontSize: 16, color: '#f2a516' }
})

const FotoActions = styled('div')({
    display: 'flex',
    flexWrap: 'wrap',
    gap: 8,
    marginTop: 12,
    flexShrink: 0
})

type FotoCardViewProps = {
    className?: string
    scope: FotoScope
}

class FotoCardViewClass implements FCClassContext<FotoCardViewProps> {
    scope!: FotoScope

    onSyncState({ scope }: FotoCardViewProps) {
        this.scope = scope
    }

    render({ className, scope }: FotoCardViewProps) {
        const row = scope.estabelecimento
        const temCoordenada = row?.lat !== null && row?.lat !== undefined && row?.lon !== null
        return (
            <TcmCard className={className} caption="Foto Web">
                {!row && (
                    <FotoBox>
                        <FotoVazia>Selecione um estabelecimento na listagem ou no mapa.</FotoVazia>
                    </FotoBox>
                )}
                {!!row && (
                    <>
                        <FotoBox>
                            {row.fotoUrl && !scope.fotoFalhou ? (
                                <img
                                    key={row.fotoUrl}
                                    src={row.fotoUrl}
                                    alt={row.estabelecimento}
                                    referrerPolicy="no-referrer"
                                    onError={() => scope.onFotoErro()}
                                />
                            ) : (
                                <FotoVazia>
                                    <ImageNotSupportedOutlinedIcon />
                                    {row.fotoUrl
                                        ? 'A foto coletada não está mais disponível no Google.'
                                        : 'Estabelecimento sem foto no Google Maps.'}
                                </FotoVazia>
                            )}
                        </FotoBox>
                        <FotoInfo>
                            <FotoNome>{row.estabelecimento}</FotoNome>
                            <FotoMeta>
                                <span>{row.categoria}</span>
                                {row.nota !== null && (
                                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}>
                                        <StarRoundedIcon />
                                        {String(row.nota).replace('.', ',')}
                                        {row.avaliacoes !== null && ` (${formatNumber(row.avaliacoes)})`}
                                    </span>
                                )}
                                {row.aberto24h && <span>Aberto 24h</span>}
                            </FotoMeta>
                            <FotoMeta>{row.endereco}</FotoMeta>
                        </FotoInfo>
                        <FotoActions>
                            {temCoordenada && (
                                <Button
                                    sx={buttonSx('default', true)}
                                    endIcon={<OpenInNewIcon />}
                                    href={`https://www.google.com/maps/@?api=1&map_action=pano&viewpoint=${row.lat},${row.lon}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    StreetView
                                </Button>
                            )}
                            {row.avaliacoesUrl && (
                                <Button
                                    sx={buttonSx('default', true)}
                                    endIcon={<OpenInNewIcon />}
                                    href={row.avaliacoesUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    Avaliações
                                </Button>
                            )}
                            {temCoordenada && (
                                <Button
                                    sx={buttonSx('default', true)}
                                    endIcon={<OpenInNewIcon />}
                                    href={`https://www.google.com/maps/search/?api=1&query=${row.lat},${row.lon}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    Google Maps
                                </Button>
                            )}
                        </FotoActions>
                    </>
                )}
            </TcmCard>
        )
    }
}

export const FotoCardView = classToFComponent(FotoCardViewClass, React)

// ::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::
// :: Células
// ::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::

const CellLink = styled('a')({
    color: LifesHubColors.green800,
    fontWeight: 600,
    textDecoration: 'none',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    '&:hover': { textDecoration: 'underline' }
})

export function normalizeUrl(value: string) {
    const url = value.trim()
    if (!url) return ''
    return /^https?:\/\//i.test(url) ? url : `https://${url}`
}

export function renderLink(value: unknown, label?: string) {
    const text = value ? String(value) : ''
    if (!text) return ''
    const href = normalizeUrl(text)
    return (
        <CellLink
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            title={text}
            onClick={(evt) => evt.stopPropagation()}
        >
            {label ?? text.replace(/^https?:\/\/(www\.)?/i, '').replace(/\/$/, '')}
        </CellLink>
    )
}

export const CardActionButton = styled(Button)(buttonSx('default', true))

export const classNames = clsx
